'use client'

import { motion } from 'framer-motion'
import WhyChooseUs from '@/components/WhyChooseUs'
import About from '@/components/About'
import { Mail, Phone, MapPin, Award, Users, CheckCircle, Target, Zap, Shield, Building2, Calendar, Trophy } from 'lucide-react'
import Link from 'next/link'
import SmartImage from '@/components/ui/SmartImage'
import { siteImages } from '@/data/siteImages'
import { useCompanyInfo } from '@/contexts/CompanyInfoContext'

export default function AboutPage() {
  const { companyInfo, loading } = useCompanyInfo()

  // --- Real, data-driven values (no invented content) ---
  const name = companyInfo?.companyName || ''
  const tagline = companyInfo?.tagline || ''
  const description = companyInfo?.businessInfo?.description || ''
  const establishedYear = companyInfo?.businessInfo?.establishedYear
  const employeeCount = companyInfo?.additionalInfo?.employeeCount || ''
  const officeLocations = companyInfo?.additionalInfo?.officeLocations?.filter(Boolean) || []
  const certifications = companyInfo?.additionalInfo?.certifications?.filter(Boolean) || []
  const awards = companyInfo?.additionalInfo?.awards?.filter(Boolean) || []
  const email = companyInfo?.contact?.email || ''
  const phone = companyInfo?.contact?.phone || ''
  const fullAddress = companyInfo?.address?.fullAddress ||
    [companyInfo?.address?.street, companyInfo?.address?.city, companyInfo?.address?.state, companyInfo?.address?.country]
      .filter(Boolean).join(', ')

  const currentYear = new Date().getFullYear()
  const yearsInBusiness = establishedYear && establishedYear <= currentYear ? currentYear - establishedYear : null

  // Build stats only from real data that exists.
  const stats: { value: string; label: string }[] = []
  if (yearsInBusiness !== null) stats.push({ value: yearsInBusiness === 0 ? 'New' : `${yearsInBusiness}+`, label: yearsInBusiness === 0 ? 'Launched This Year' : 'Years in Business' })
  if (establishedYear) stats.push({ value: String(establishedYear), label: 'Established' })
  if (employeeCount) stats.push({ value: employeeCount, label: 'Team Members' })
  if (officeLocations.length) stats.push({ value: `${officeLocations.length}`, label: officeLocations.length === 1 ? 'Office' : 'Offices' })
  if (certifications.length) stats.push({ value: `${certifications.length}`, label: 'Certifications' })

  const companyValues = [
    { icon: <Target className="w-8 h-8" />, title: 'Our Mission', description: 'Deliver reliable, well-engineered software that helps our clients grow and operate with confidence.' },
    { icon: <Zap className="w-8 h-8" />, title: 'Innovation', description: 'We choose the right tools for each problem and build for performance, scale, and security from day one.' },
    { icon: <Shield className="w-8 h-8" />, title: 'Quality First', description: 'Clean code, careful testing, and clear communication on every engagement — no shortcuts.' },
    { icon: <Users className="w-8 h-8" />, title: 'Partnership', description: 'We work as an extension of your team, with transparency and long-term relationships at the core.' },
  ]

  if (loading) {
    return (
      <main className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </main>
    )
  }

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                About <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{name || 'Us'}</span>
              </h1>
              {tagline && (
                <h2 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">{tagline}</h2>
              )}
              {description && (
                <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">{description}</p>
              )}
            </motion.div>
          </div>

          {/* Stats Grid — only shown when we have real data */}
          {stats.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className={`grid grid-cols-2 gap-6 mb-16 max-w-5xl mx-auto ${
                { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' }[Math.min(stats.length, 5)] || 'md:grid-cols-4'
              }`}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center bg-white rounded-2xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Photo collage band */}
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { src: siteImages.about.office, span: 'md:col-span-2 md:row-span-2 h-56 md:h-full', label: 'Our Workspace' },
              { src: siteImages.about.team, span: 'h-40', label: 'The Team' },
              { src: siteImages.about.culture, span: 'h-40', label: 'Our Culture' },
              { src: siteImages.hero.secondary, span: 'md:col-span-2 h-40', label: 'Building Together' },
            ].map((item, i) => (
              <motion.div key={i} whileHover={{ scale: 1.015 }} className={`group relative rounded-2xl overflow-hidden shadow-lg ${item.span}`}>
                <SmartImage src={item.src} alt={item.label} fill sizes="(max-width: 768px) 50vw, 25vw"
                  className="object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                <span className="absolute bottom-3 left-4 text-white font-semibold text-sm drop-shadow">{item.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact + Company facts */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details (real) */}
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Get In Touch</h2>

              {email && (
                <div className="flex items-center mb-5 p-4 bg-blue-50 rounded-xl">
                  <Mail className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-700 break-all">{email}</a>
                  </div>
                </div>
              )}

              {phone && (
                <div className="flex items-center mb-5 p-4 bg-purple-50 rounded-xl">
                  <Phone className="w-6 h-6 text-purple-600 mr-4 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <a href={`tel:${phone}`} className="text-purple-600 hover:text-purple-700">{phone}</a>
                  </div>
                </div>
              )}

              {fullAddress && (
                <div className="flex items-start mb-8 p-4 bg-emerald-50 rounded-xl">
                  <MapPin className="w-6 h-6 text-emerald-600 mr-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">Address</div>
                    <div className="text-gray-700">{fullAddress}</div>
                  </div>
                </div>
              )}

              {/* Office locations (real, if provided) */}
              {officeLocations.length > 0 && (
                <>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Our Offices</h3>
                  <div className="space-y-3">
                    {officeLocations.map((office, index) => (
                      <div key={index} className="flex items-center p-4 border border-gray-200 rounded-xl">
                        <Building2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
                        <div className="text-gray-700 font-medium">{office}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>

            {/* Company facts / certifications / awards (real, if provided) */}
            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Company at a Glance</h3>

                <div className="space-y-3 mb-6">
                  {name && (
                    <div className="flex items-center bg-white p-4 rounded-xl">
                      <Building2 className="w-5 h-5 text-blue-600 mr-3" />
                      <div><span className="text-sm text-gray-500">Company</span><div className="font-semibold text-gray-900">{name}</div></div>
                    </div>
                  )}
                  {establishedYear && (
                    <div className="flex items-center bg-white p-4 rounded-xl">
                      <Calendar className="w-5 h-5 text-purple-600 mr-3" />
                      <div><span className="text-sm text-gray-500">Established</span><div className="font-semibold text-gray-900">{establishedYear}</div></div>
                    </div>
                  )}
                  {employeeCount && (
                    <div className="flex items-center bg-white p-4 rounded-xl">
                      <Users className="w-5 h-5 text-emerald-600 mr-3" />
                      <div><span className="text-sm text-gray-500">Team Size</span><div className="font-semibold text-gray-900">{employeeCount}</div></div>
                    </div>
                  )}
                </div>

                {certifications.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center"><Award className="w-5 h-5 text-blue-600 mr-2" />Certifications</h4>
                    <div className="space-y-2">
                      {certifications.map((cert, i) => (
                        <div key={i} className="flex items-center bg-white p-3 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-800">{cert}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {awards.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center"><Trophy className="w-5 h-5 text-amber-500 mr-2" />Awards</h4>
                    <div className="space-y-2">
                      {awards.map((award, i) => (
                        <div key={i} className="flex items-center bg-white p-3 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-amber-500 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-800">{award}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {certifications.length === 0 && awards.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Certifications and awards will appear here once added in Settings → Company Information.
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Component */}
      <About />

      {/* Company Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">The principles that guide how we work with every client.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Component */}
      <WhyChooseUs />

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Ready to Work Together?</h2>
            <p className="text-lg lg:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              {name ? `Let's discuss how ${name} can help you build and grow.` : "Let's discuss how we can help you build and grow."}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300">Get In Touch</Link>
              <Link href="/services" className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">View Our Services</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
